import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FixedSizeGrid } from 'react-window';
import { getSocket, isSocketConnected, onSocketChange } from '../socket';
import { auth, uploadFile, compressMediaForChat, compressImageForUpload } from '../firebase';
import { resolveServerUrl } from '../utils/serverUrl';
import ChatWindow from './ChatWindow';
import ProjectFilePreview from './ProjectFilePreview';
import ProjectSettings from './ProjectSettings';
import ProjectOverview from './ProjectOverview';
import ProjectAssetBoard from './ProjectAssetBoard';
import ProjectTaskBoard from './ProjectTaskBoard';
import ProjectGameView from './ProjectGameView';
import FitText from './FitText';
import './ProjectView.css';

// Tabs hidden from the project view for now. The tab button, the landing-tab
// resolver and the body panel all read this one list, so removing a key here
// brings the tab back in full — no other edits needed. The components and
// their render blocks are left intact on purpose.
const HIDDEN_TABS = ['overview', 'tasks'];
const isHiddenTab = (key) => HIDDEN_TABS.includes(key);

// File category detection (matches server logic)
const detectCategory = (fileName, contentType) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  const model3dExts = ['obj', 'fbx', 'gltf', 'glb', 'stl', '3ds', 'blend', 'ply'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  if (contentType?.startsWith('image/')) return 'image';
  if (contentType?.startsWith('video/')) return 'video';
  if (model3dExts.includes(ext)) return '3d';
  if (contentType === 'application/pdf' || ext === 'pdf') return 'document';
  if (['doc', 'docx', 'txt', 'md', 'csv', 'xls', 'xlsx'].includes(ext)) return 'document';
  if (archiveExts.includes(ext)) return 'archive';
  return 'other';
};

const categoryIcons = {
  image: { icon: 'image', color: '#4CAF50' },
  video: { icon: 'videocam', color: '#FF5722' },
  '3d': { icon: 'view_in_ar', color: '#3BA6FF' },
  document: { icon: 'description', color: '#4285F4' },
  archive: { icon: 'folder_zip', color: '#FF9800' },
  other: { icon: 'insert_drive_file', color: '#9A9FA5' }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// One card in the file grid. Memoized on fileId so unrelated file updates
// don't re-render the whole grid. All callbacks come in via `api` so the
// component stays a pure function of its props.
const FileCard = React.memo(function FileCard({ file, api, user, dragFileId, dropFolderId }) {
  const cat = categoryIcons[file.category] || categoryIcons.other;
  const isImage = file.category === 'image';
  const isVideo = file.category === 'video';
  const canFeature = isImage || isVideo;

  return (
    <div
      className={`pv-file-card ${file.seenBy && !file.seenBy.includes(user.uid) ? 'unseen' : ''} ${api.selectedFiles.has(file.fileId) ? 'selected' : ''} ${dragFileId === file.fileId ? 'dragging' : ''}`}
      onClick={() => api.openPreview(file)}
      draggable
      onDragStart={(e) => {
        api.setDragFileId(file.fileId);
        e.dataTransfer.setData('text/x-tchat-file', file.fileId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => { api.setDragFileId(null); api.setDropFolderId(undefined); }}
    >
      <label
        className={`pv-file-checkbox ${api.selectedFiles.has(file.fileId) ? 'checked' : ''}`}
        onClick={(e) => api.toggleFileSelection(file.fileId, e)}
      >
        <span className="material-symbols-outlined">
          {api.selectedFiles.has(file.fileId) ? 'check_box' : 'check_box_outline_blank'}
        </span>
      </label>
      <div className="pv-file-thumb">
        {isImage && (file.thumbUrl || file.storageUrl) ? (
          <img
            src={file.thumbUrl || file.storageUrl}
            alt={file.fileName}
            loading="lazy"
            decoding="async"
          />
        ) : isVideo && file.storageUrl ? (
          <video src={file.storageUrl} preload="metadata" muted />
        ) : (
          <span
            className="material-symbols-outlined pv-file-type-icon"
            style={{ color: cat.color }}
          >
            {cat.icon}
          </span>
        )}
      </div>
      <div className="pv-file-info">
        <FitText className="pv-file-name" text={file.fileName} max={12} min={8.5} />
        <span className="pv-file-meta">
          {formatFileSize(file.fileSize)}
          {file.uploadedAt ? ` · ${formatDate(file.uploadedAt)}` : ''}
        </span>
      </div>
      {file.featured && (
        <span className="pv-file-featured-badge material-symbols-outlined" title="Featured in gallery">star</span>
      )}
      <div className="pv-file-hover-actions">
        {canFeature && (
          <button
            className={`pv-file-action-btn pv-action-feature ${file.featured ? 'featured' : ''}`}
            onClick={(e) => api.handleFeatureFile(file, e)}
            title={file.featured ? 'Remove from gallery' : 'Feature in gallery'}
          >
            <span className="material-symbols-outlined">
              {file.featured ? 'star' : 'star_outline'}
            </span>
          </button>
        )}
        <button
          className="pv-file-action-btn pv-action-move"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            api.setMoveMenu({ fileIds: api.dragPayload(file.fileId), x: rect.left, y: rect.bottom + 4 });
          }}
          title="Move to folder"
        >
          <span className="material-symbols-outlined">drive_file_move</span>
        </button>
        <button
          className="pv-file-action-btn pv-action-react"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            api.setReactionPicker({ fileId: file.fileId, x: rect.left, y: rect.bottom + 4 });
          }}
          title="React"
        >
          <span className="material-symbols-outlined">add_reaction</span>
        </button>
        <a
          className="pv-file-action-btn pv-action-download"
          href={file.storageUrl}
          download={file.fileName}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Download"
        >
          <span className="material-symbols-outlined">download</span>
        </a>
        <button
          className="pv-file-action-btn pv-action-delete"
          onClick={(e) => { e.stopPropagation(); api.setDeleteConfirm(file.fileId); }}
          title="Delete file"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
      {api.deleteConfirm === file.fileId && (
        <div className="pv-delete-confirm" onClick={(e) => e.stopPropagation()}>
          <span>Delete this file?</span>
          <div className="pv-delete-confirm-actions">
            <button className="pv-confirm-yes" onClick={() => { api.handleDeleteFile(file.fileId); api.setDeleteConfirm(null); }}>Delete</button>
            <button className="pv-confirm-no" onClick={() => api.setDeleteConfirm(null)}>Cancel</button>
          </div>
        </div>
      )}
      {(file.comments?.length > 0) && (
        <span className="pv-file-comment-badge">
          <span className="material-symbols-outlined">comment</span>
          {file.comments.length}
        </span>
      )}
      {file.reactions && Object.keys(file.reactions).length > 0 && (
        <div className="pv-file-reactions" onClick={(e) => e.stopPropagation()}>
          {Object.entries(file.reactions).map(([emoji, userIds]) => (
            <button
              key={emoji}
              className={`pv-reaction-chip ${userIds.includes(user.uid) ? 'reacted' : ''}`}
              onClick={() => api.handleFileReact(file.fileId, emoji)}
              title={userIds.length + (userIds.length === 1 ? ' reaction' : ' reactions')}
            >
              <span>{emoji}</span>
              {userIds.length > 1 && <span className="pv-reaction-count">{userIds.length}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}, (prev, next) => prev.file === next.file && prev.dragFileId === next.dragFileId && prev.api === next.api);

// Virtualized grid for the file list. Without this, projects with hundreds of
// files render one <img> per file up front and the browser chokes. The grid
// only mounts cells within the scroll viewport plus a small overscan.
const CARD_MIN_WIDTH = 160;
const CARD_GAP = 12;
const CARD_ROW_HEIGHT = 180;

function FileGrid({ files, user, api, dragFileId, dropFolderId }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const r = entries[0].contentRect;
      if (r.width > 0 && r.height > 0) setSize({ width: r.width, height: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Column count mirrors the previous CSS rule (auto-fill 160px, gap 12px).
  const cols = Math.max(1, Math.floor((size.width + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)));
  const columnWidth = Math.floor((size.width - CARD_GAP * (cols - 1)) / cols);
  const rowCount = Math.ceil(files.length / cols);

  return (
    <div ref={containerRef} className="pv-file-grid-virtual">
      <FixedSizeGrid
        columnCount={cols}
        rowCount={rowCount}
        columnWidth={columnWidth}
        rowHeight={CARD_ROW_HEIGHT}
        width={size.width}
        height={size.height}
        overscanRowCount={2}
      >
        {({ columnIndex, rowIndex, style }) => {
          const idx = rowIndex * cols + columnIndex;
          if (idx >= files.length) return null;
          const file = files[idx];
          return (
            <div style={{ ...style, padding: 6, boxSizing: 'border-box' }}>
              <FileCard
                file={file}
                api={api}
                user={user}
                dragFileId={dragFileId}
              />
            </div>
          );
        }}
      </FixedSizeGrid>
    </div>
  );
}

function ProjectView({ user, project, friends, myBubbleColor, onBack, messages, setMessages, loadingMessagesByRoom, paginationByRoom, onLoadOlderMessages, unreadDetail }) {
  const [activeTab, setActiveTab] = useState(null); // resolved after data loads
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = "All Files"
  const [chatRoom, setChatRoom] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [uploadProgress, setUploadProgress] = useState({}); // fileKey -> { percent, name }
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [reactionPicker, setReactionPicker] = useState(null); // { fileId, x, y }
  const [moveMenu, setMoveMenu] = useState(null); // { fileIds, x, y }
  const [dragFileId, setDragFileId] = useState(null); // internal file-card drag
  const [dropFolderId, setDropFolderId] = useState(undefined); // folder highlighted as drop target
  const [deleteConfirm, setDeleteConfirm] = useState(null); // fileId
  const [selectedFiles, setSelectedFiles] = useState(new Set()); // Set of fileIds for multi-select
  const [massDeleteConfirm, setMassDeleteConfirm] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [zipDownloadError, setZipDownloadError] = useState('');
  const fileInputRef = useRef(null);
  const filesLoadedRef = useRef(false);
  const quickEmojis = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F525}', '\u{1F440}', '\u{1F60D}', '\u{1F44F}'];

  // Whether this project uses the game-dev tabs (Overview + Assets)
  const hasGameDev = !!(project?.features?.assetBoard || assets.length > 0 || project?.progress);

  // Bumped whenever the socket is replaced or (re)connects, so the effects below re-bind their
  // listeners to the live socket and refetch whatever changed while we were disconnected.
  // Without this, opening a project mid-reconnect left it empty and it went deaf afterwards.
  const [socketEpoch, setSocketEpoch] = useState(0);
  useEffect(() => {
    let current = null;
    const bump = () => setSocketEpoch(e => e + 1);
    const unsubscribe = onSocketChange((s) => {
      if (current === s) return;
      if (current) {
        current.off('connect', bump);
        bump();
      }
      current = s;
      s.on('connect', bump);
    });
    return () => {
      unsubscribe();
      if (current) current.off('connect', bump);
    };
  }, []);

  // Leaving a project marks its files seen (as the Android app does on open). Doing it on
  // leave keeps the "new" markers visible while the user is looking at the project.
  const openProjectId = project?.projectId;
  useEffect(() => {
    if (!openProjectId) return;
    return () => {
      const s = getSocket();
      if (s) s.emit('project:files:seen', { projectId: openProjectId });
    };
  }, [openProjectId]);

  // Load folders, files and assets when project changes
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !project) return;

    // Listeners are bound even while disconnected; the 'connect' bump re-runs this to fetch.
    filesLoadedRef.current = false;
    if (isSocketConnected()) {
      socket.emit('project:folders:get', { projectId: project.projectId });
      socket.emit('project:files:get', { projectId: project.projectId });
      socket.emit('project:assets:get', { projectId: project.projectId });
    }

    // Set up chat room reference
    if (project.chatRoomId) {
      setChatRoom({
        roomId: project.chatRoomId,
        name: project.name + ' Chat',
        members: project.members,
        type: 'project'
      });
    }

    const handleFoldersList = ({ projectId, folders: f }) => {
      if (projectId === project.projectId) setFolders(f || []);
    };
    const handleFilesList = ({ projectId, files: f }) => {
      if (projectId === project.projectId) {
        setFiles(f || []);
        filesLoadedRef.current = true;
      }
    };
    const handleAssetsList = ({ projectId, assets: a }) => {
      if (projectId === project.projectId) setAssets(a || []);
    };
    const handleAssetCreated = (asset) => {
      if (asset.projectId === project.projectId) {
        setAssets(prev => prev.some(a => a.assetId === asset.assetId) ? prev : [...prev, asset]);
      }
    };
    const handleAssetUpdated = ({ projectId, assetId, updates }) => {
      if (projectId === project.projectId) {
        setAssets(prev => prev.map(a => a.assetId === assetId ? { ...a, ...updates } : a));
      }
    };
    const handleAssetDeleted = ({ projectId, assetId }) => {
      if (projectId === project.projectId) {
        setAssets(prev => prev.filter(a => a.assetId !== assetId));
      }
    };
    const handleFolderCreated = (folder) => {
      if (folder.projectId === project.projectId) {
        setFolders(prev => [...prev, folder]);
      }
    };
    const handleFolderUpdated = ({ projectId, folderId, name }) => {
      if (projectId === project.projectId) {
        setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, name } : f));
      }
    };
    const handleFolderDeleted = ({ projectId, folderId }) => {
      if (projectId === project.projectId) {
        setFolders(prev => prev.filter(f => f.folderId !== folderId));
        // Files in deleted folder moved to root
        setFiles(prev => prev.map(f => f.folderId === folderId ? { ...f, folderId: null } : f));
        setSelectedFolderId(prev => prev === folderId ? null : prev);
      }
    };
    const handleFileUploaded = (file) => {
      if (file.projectId === project.projectId) {
        setFiles(prev => {
          if (prev.some(f => f.fileId === file.fileId)) return prev;
          return [file, ...prev];
        });
      }
    };
    const handleFileMoved = ({ projectId, fileId, folderId }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => f.fileId === fileId ? { ...f, folderId } : f));
      }
    };
    const handleFileDeleted = ({ projectId, fileId }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.filter(f => f.fileId !== fileId));
      }
    };
    const handleFileFeatured = ({ projectId, fileId, featured }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => f.fileId === fileId ? { ...f, featured } : f));
      }
    };
    const handleCommentAdded = ({ projectId, fileId, comment }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => {
          if (f.fileId !== fileId) return f;
          return { ...f, comments: [...(f.comments || []), comment] };
        }));
      }
    };
    const handleCommentDeleted = ({ projectId, fileId, commentId }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => {
          if (f.fileId !== fileId) return f;
          return { ...f, comments: (f.comments || []).filter(c => c.commentId !== commentId) };
        }));
      }
    };
    const handleFileReaction = ({ projectId, fileId, reactions }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => f.fileId === fileId ? { ...f, reactions } : f));
      }
    };
    const handleFileSeenUpdated = ({ projectId, fileId, userId }) => {
      if (projectId === project.projectId) {
        setFiles(prev => prev.map(f => {
          if (f.fileId !== fileId) return f;
          if (f.seenBy?.includes(userId)) return f;
          return { ...f, seenBy: [...(f.seenBy || []), userId] };
        }));
      }
    };

    socket.on('project:folders:list', handleFoldersList);
    socket.on('project:files:list', handleFilesList);
    socket.on('project:assets:list', handleAssetsList);
    socket.on('project:asset:created', handleAssetCreated);
    socket.on('project:asset:updated', handleAssetUpdated);
    socket.on('project:asset:deleted', handleAssetDeleted);
    socket.on('project:folder:created', handleFolderCreated);
    socket.on('project:folder:updated', handleFolderUpdated);
    socket.on('project:folder:deleted', handleFolderDeleted);
    socket.on('project:file:uploaded', handleFileUploaded);
    socket.on('project:file:moved', handleFileMoved);
    socket.on('project:file:deleted', handleFileDeleted);
    socket.on('project:file:featured', handleFileFeatured);
    socket.on('project:file:comment:added', handleCommentAdded);
    socket.on('project:file:comment:deleted', handleCommentDeleted);
    socket.on('project:file:reaction', handleFileReaction);
    socket.on('project:file:seen:updated', handleFileSeenUpdated);

    return () => {
      socket.off('project:folders:list', handleFoldersList);
      socket.off('project:files:list', handleFilesList);
      socket.off('project:assets:list', handleAssetsList);
      socket.off('project:asset:created', handleAssetCreated);
      socket.off('project:asset:updated', handleAssetUpdated);
      socket.off('project:asset:deleted', handleAssetDeleted);
      socket.off('project:folder:created', handleFolderCreated);
      socket.off('project:folder:updated', handleFolderUpdated);
      socket.off('project:folder:deleted', handleFolderDeleted);
      socket.off('project:file:uploaded', handleFileUploaded);
      socket.off('project:file:moved', handleFileMoved);
      socket.off('project:file:deleted', handleFileDeleted);
      socket.off('project:file:featured', handleFileFeatured);
      socket.off('project:file:comment:added', handleCommentAdded);
      socket.off('project:file:comment:deleted', handleCommentDeleted);
      socket.off('project:file:reaction', handleFileReaction);
      socket.off('project:file:seen:updated', handleFileSeenUpdated);
    };
  }, [project, socketEpoch]);

  // Landing tab: the tab they last used for this project, else Assets for
  // game-dev projects, else Files.
  useEffect(() => {
    if (activeTab !== null || !project) return;
    const gameDev = !!(project.features?.assetBoard || project.progress || project.game);
    let stored = null;
    try { stored = localStorage.getItem('pv_tab_' + project.projectId); } catch (_) {}
    const validTabs = {
      overview: gameDev,
      assets: gameDev,
      game: !!project.game,
      sky: !!project.features?.skyMap,
      tasks: true,
      files: true,
      chat: !!project.chatRoomId
    };
    // A hidden tab is never a valid landing tab, so a remembered "overview"
    // from before it was hidden falls through to Assets instead of a blank pane.
    HIDDEN_TABS.forEach(k => { validTabs[k] = false; });
    if (stored && validTabs[stored]) { setActiveTab(stored); return; }
    if (validTabs.sky) { setActiveTab('sky'); return; }
    if (gameDev) { setActiveTab('assets'); return; }
    if (!filesLoadedRef.current && files.length === 0) return; // wait for first files list
    if (assets.length > 0) setActiveTab('assets');
    else setActiveTab('files');
  }, [activeTab, project, files, assets]);

  // Remember the tab per project so it reopens where they left off
  useEffect(() => {
    if (!activeTab || !project) return;
    try { localStorage.setItem('pv_tab_' + project.projectId, activeTab); } catch (_) {}
  }, [activeTab, project]);

  // Fallback: if nothing loads within 2s, land on Files
  useEffect(() => {
    if (activeTab !== null) return;
    const t = setTimeout(() => setActiveTab(prev => prev === null ? 'files' : prev), 2000);
    return () => clearTimeout(t);
  }, [activeTab]);

  // Request chat room messages when chat tab is opened
  useEffect(() => {
    if (activeTab !== 'chat' || !chatRoom) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('room:messages:get', { roomId: chatRoom.roomId });
    }
  }, [activeTab, chatRoom, socketEpoch]);

  // While the Chat tab is open, everything loaded in it counts as read (the Android app does
  // the same on open). The message list only marks bubbles that scroll into view, which left
  // older unread ones counting against the Projects badge after leaving.
  const seenRequestedRef = useRef(new Set());
  const chatMessages = chatRoom ? messages?.[chatRoom.roomId] : null;
  useEffect(() => {
    if (activeTab !== 'chat' || !chatRoom || !chatMessages?.length) return;
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;
    for (const m of chatMessages) {
      if (!m.id || m.senderId === user.uid || m.seenBy?.includes(user.uid)) continue;
      if (String(m.id).startsWith('placeholder_') || seenRequestedRef.current.has(m.id)) continue;
      seenRequestedRef.current.add(m.id);
      socket.emit('message:seen', { roomId: chatRoom.roomId, messageId: m.id });
    }
  }, [activeTab, chatRoom, chatMessages, user.uid, socketEpoch]);

  const filteredFiles = selectedFolderId === null
    ? files
    : selectedFolderId === '__root__'
      ? files.filter(f => !f.folderId)
      : files.filter(f => f.folderId === selectedFolderId);

  const unseenCount = useMemo(
    () => files.filter(f => f.seenBy && !f.seenBy.includes(user.uid)).length,
    [files, user.uid]
  );

  // Clear selection when folder changes
  useEffect(() => {
    setSelectedFiles(new Set());
  }, [selectedFolderId]);

  // Folder CRUD
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:folder:create', { projectId: project.projectId, name: newFolderName.trim() });
    }
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const handleRenameFolder = (folderId) => {
    if (!renameValue.trim()) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:folder:rename', { projectId: project.projectId, folderId, name: renameValue.trim() });
    }
    setRenamingFolder(null);
    setRenameValue('');
  };

  const handleDeleteFolder = (folderId) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:folder:delete', { projectId: project.projectId, folderId });
    }
    setFolderContextMenu(null);
  };

  // File upload (with small thumbnail generation for images)
  const handleFileUpload = useCallback(async (fileList) => {
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;

    for (const rawFile of Array.from(fileList)) {
      const uploadKey = Date.now() + '_' + rawFile.name;
      setUploadProgress(prev => ({ ...prev, [uploadKey]: { percent: 0, name: rawFile.name } }));

      try {
        // Compress images/videos if applicable
        let file = rawFile;
        const category = detectCategory(rawFile.name, rawFile.type);
        if (category === 'image' && !rawFile.type.includes('gif')) {
          try {
            const compressed = await compressMediaForChat(rawFile, {
              onProgress: (p) => setUploadProgress(prev => ({
                ...prev, [uploadKey]: { ...prev[uploadKey], percent: Math.round(p * 0.3) }
              }))
            });
            if (compressed && compressed.size < rawFile.size) file = compressed;
          } catch (_) {}
        }

        const folderId = selectedFolderId === null || selectedFolderId === '__root__' ? null : selectedFolderId;
        const folderPath = folderId || 'root';
        const stamp = Date.now();
        const storagePath = `project-files/${project.projectId}/${folderPath}/${stamp}_${file.name}`;

        const storageUrl = await uploadFile(file, storagePath, {
          contentType: file.type,
          onProgress: (pct) => setUploadProgress(prev => ({
            ...prev, [uploadKey]: { ...prev[uploadKey], percent: 30 + Math.round(pct * 0.65) }
          }))
        });

        // Small grid thumbnail so lists never download full-size images
        let thumbUrl = null;
        let thumbPath = null;
        if (category === 'image' && !rawFile.type.includes('gif')) {
          try {
            const thumb = await compressImageForUpload(rawFile, { maxDimension: 360, quality: 0.72 });
            if (thumb && thumb !== rawFile && thumb.size < file.size) {
              thumbPath = `project-files/${project.projectId}/thumbs/${stamp}_${file.name}.webp`;
              thumbUrl = await uploadFile(thumb, thumbPath, { contentType: thumb.type });
            }
          } catch (_) {}
        }

        // Register file metadata on server
        socket.emit('project:file:upload', {
          projectId: project.projectId,
          folderId,
          fileName: rawFile.name,
          fileSize: file.size,
          contentType: file.type,
          storageUrl,
          storagePath,
          thumbUrl,
          thumbPath
        });

        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[uploadKey];
          return next;
        });
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[uploadKey];
          return next;
        });
      }
    }
  }, [project, selectedFolderId]);

  // File delete
  const handleDeleteFile = useCallback((fileId) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:file:delete', { projectId: project.projectId, fileId });
    }
  }, [project]);

  // Multi-select toggle
  const toggleFileSelection = useCallback((fileId, e) => {
    e.stopPropagation();
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  // Select all visible files
  const selectAllFiles = () => {
    setSelectedFiles(new Set(filteredFiles.map(f => f.fileId)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFiles(new Set());
  };

  // Mass delete selected files
  const handleMassDelete = () => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      selectedFiles.forEach(fileId => {
        socket.emit('project:file:delete', { projectId: project.projectId, fileId });
      });
    }
    setSelectedFiles(new Set());
    setMassDeleteConfirm(false);
  };

  // Mass download selected files
  const handleMassDownload = () => {
    const selectedFilesList = filteredFiles.filter(f => selectedFiles.has(f.fileId));
    selectedFilesList.forEach((file, index) => {
      // Stagger downloads to avoid browser blocking
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = file.storageUrl;
        link.download = file.fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 200);
    });
  };

  // Download every file in the project as one server-streamed ZIP. On browsers
  // with the File System Access API, stream straight to disk instead of holding
  // a potentially large archive in memory; other browsers use a Blob fallback.
  const handleDownloadAllZip = async () => {
    if (isDownloadingZip || files.length === 0) return;
    setIsDownloadingZip(true);
    setZipDownloadError('');

    const safeProjectName = (project.name || 'project-files')
      .split('').map(char => char.charCodeAt(0) < 32 ? '_' : char).join('')
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/[. ]+$/g, '') || 'project-files';
    const zipName = `${safeProjectName}.zip`;
    let fileHandle = null;

    try {
      if (typeof window.showSaveFilePicker === 'function') {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: zipName,
          types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }]
        });
      }

      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('You need to sign in again before downloading files.');
      const response = await fetch(
        `${resolveServerUrl()}/api/projects/${encodeURIComponent(project.projectId)}/files.zip`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'The project ZIP could not be downloaded.');
      }

      if (fileHandle && response.body) {
        const writable = await fileHandle.createWritable();
        await response.body.pipeTo(writable);
      } else {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = zipName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Project ZIP download failed:', error);
        setZipDownloadError(error?.message || 'The project ZIP could not be downloaded.');
      }
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // File reaction
  const handleFileReact = useCallback((fileId, emoji) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:file:react', { projectId: project.projectId, fileId, emoji });
    }
    setReactionPicker(null);
  }, [project]);

  // Move files to a folder (folderId null = Unsorted/root)
  const moveFiles = useCallback((fileIds, folderId) => {
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;
    fileIds.forEach(fileId => {
      socket.emit('project:file:move', { projectId: project.projectId, fileId, folderId });
    });
    setMoveMenu(null);
    setSelectedFiles(new Set());
  }, [project]);

  // Which files an internal drag carries: the dragged card, plus the whole
  // selection when the dragged card is part of it
  const dragPayload = useCallback((fileId) =>
    selectedFiles.has(fileId) ? [...selectedFiles] : [fileId]
  , [selectedFiles]);

  // (fileCardApi is declared later, after openPreview, to keep its dep
  // declarations in source order and avoid the use-before-define lint hit.)

  // Feature/unfeature a file (Gallery)
  const handleFeatureFile = useCallback((file, e) => {
    e.stopPropagation();
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:file:feature', {
        projectId: project.projectId,
        fileId: file.fileId,
        featured: !file.featured
      });
    }
  }, [project]);

  const openPreview = useCallback((file) => {
    setPreviewFile(file);
    // Mark file as seen
    if (file.seenBy && !file.seenBy.includes(user.uid)) {
      const s = getSocket();
      if (s && isSocketConnected()) {
        s.emit('project:file:seen', { projectId: project.projectId, fileId: file.fileId });
      }
      // Optimistic update
      setFiles(prev => prev.map(f =>
        f.fileId === file.fileId ? { ...f, seenBy: [...(f.seenBy || []), user.uid] } : f
      ));
    }
  }, [project, user.uid]);

  // Stable bag of callbacks for FileCard. Recomputing this would defeat the
  // React.memo on FileCard and re-render every cell on any selection change.
  const fileCardApi = useMemo(() => ({
    openPreview,
    toggleFileSelection,
    handleFeatureFile,
    handleDeleteFile,
    handleFileReact,
    dragPayload,
    setDragFileId,
    setDropFolderId,
    setMoveMenu,
    setReactionPicker,
    setDeleteConfirm,
    selectedFiles,
    deleteConfirm
  }), [
    openPreview, toggleFileSelection, handleFeatureFile, handleDeleteFile,
    handleFileReact, dragPayload,
    setDragFileId, setDropFolderId, setMoveMenu, setReactionPicker, setDeleteConfirm,
    selectedFiles, deleteConfirm
  ]);

  // Drag and drop (uploads only — internal card drags are handled by the folder tree)
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragFileId && e.dataTransfer.types?.includes('Files')) setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (dragFileId) return;
    if (e.dataTransfer.files?.length) handleFileUpload(e.dataTransfer.files);
  };

  // Close folder context menu on click outside
  useEffect(() => {
    if (!folderContextMenu) return;
    const close = () => setFolderContextMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [folderContextMenu]);

  // Close reaction picker on click outside
  useEffect(() => {
    if (!reactionPicker) return;
    const close = () => setReactionPicker(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [reactionPicker]);

  // Close move menu on click outside
  useEffect(() => {
    if (!moveMenu) return;
    const close = () => setMoveMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [moveMenu]);

  if (!project) return null;

  const tabs = [
    ...(hasGameDev ? [
      { key: 'overview', label: 'Overview', icon: 'space_dashboard' },
      { key: 'assets', label: 'Assets', icon: 'view_in_ar' }
    ] : []),
    ...(project.game ? [{ key: 'game', label: 'Game', icon: 'public' }] : []),
    ...(project.features?.skyMap ? [{ key: 'sky', label: 'Sky', icon: 'auto_awesome' }] : []),
    { key: 'tasks', label: 'Tasks', icon: 'checklist' },
    { key: 'files', label: 'Files', icon: 'folder', badge: unseenCount },
    ...(chatRoom ? [{ key: 'chat', label: 'Chat', icon: 'chat', badge: activeTab === 'chat' ? 0 : (unreadDetail?.chat || 0) }] : [])
  ].filter(tab => !isHiddenTab(tab.key));

  // Never render a hidden tab's panel, even if something set it: fall back to
  // the first visible tab.
  const resolvedTab = activeTab || 'files';
  const currentTab = isHiddenTab(resolvedTab) ? (tabs[0]?.key || 'files') : resolvedTab;

  return (
    <div className="project-view">
      {/* Header */}
      <div className="project-view-header">
        <button className="pv-back-btn" onClick={onBack} title="Back to sidebar">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="pv-header-info">
          <h2>{project.name}</h2>
          {project.description && <span className="pv-header-desc">{project.description}</span>}
        </div>
        <div className="pv-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`pv-tab ${currentTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="material-symbols-outlined">{tab.icon}</span>
              <span className="pv-tab-label">{tab.label}</span>
              {tab.badge > 0 && <span className="pv-tab-badge">{tab.badge}</span>}
            </button>
          ))}
        </div>
        <div className="pv-header-actions">
          <button
            className="pv-header-btn"
            onClick={() => setShowSettings(true)}
            title="Project settings"
          >
            <span className="material-symbols-outlined">settings</span>
          </button>
        </div>
      </div>

      <div className="project-view-body">
        {currentTab === 'overview' && (
          <div className="pv-tab-content">
            <ProjectOverview
              user={user}
              project={project}
              assets={assets}
              files={files}
              onNavigate={setActiveTab}
              onPreview={openPreview}
            />
          </div>
        )}

        {currentTab === 'assets' && (
          <div className="pv-tab-content">
            <ProjectAssetBoard
              user={user}
              project={project}
              assets={assets}
              friends={friends}
            />
          </div>
        )}

        {currentTab === 'game' && (
          <div className="pv-tab-content">
            <ProjectGameView project={project} />
          </div>
        )}

        {/* "Sky" tab: the celestial-sphere frame map, a self-contained static
            app served from /starmap/ (client/public/starmap). */}
        {currentTab === 'sky' && (
          <div className="pv-tab-content pv-sky">
            <iframe
              className="pv-sky-frame"
              src={`${process.env.PUBLIC_URL}/starmap/index.html`}
              title="Sky map"
              allow="fullscreen"
            />
          </div>
        )}

        {currentTab === 'tasks' && (
          <div className="pv-tab-content">
            <ProjectTaskBoard user={user} project={project} />
          </div>
        )}

        {currentTab === 'files' && (
          <>
            {/* Folder Tree */}
            <div className="pv-folder-tree">
              <div className="pv-folder-header">
                <span className="pv-folder-title">Folders</span>
                <button
                  className="pv-folder-add-btn"
                  onClick={() => setShowNewFolder(!showNewFolder)}
                  title="New folder"
                >
                  <span className="material-symbols-outlined">create_new_folder</span>
                </button>
              </div>

              {showNewFolder && (
                <div className="pv-new-folder">
                  <input
                    type="text"
                    placeholder="Folder name..."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                    autoFocus
                  />
                  <button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                    <span className="material-symbols-outlined">check</span>
                  </button>
                </div>
              )}

              <div
                className={`pv-folder-item ${selectedFolderId === null ? 'active' : ''}`}
                onClick={() => setSelectedFolderId(null)}
              >
                <span className="material-symbols-outlined">folder_open</span>
                <span>All Files</span>
                <span className="pv-folder-count">{files.length}</span>
              </div>

              <div
                className={`pv-folder-item ${selectedFolderId === '__root__' ? 'active' : ''} ${dragFileId && dropFolderId === null ? 'drop-target' : ''}`}
                onClick={() => setSelectedFolderId('__root__')}
                onDragOver={(e) => { if (dragFileId) { e.preventDefault(); setDropFolderId(null); } }}
                onDragLeave={() => setDropFolderId(prev => prev === null ? undefined : prev)}
                onDrop={(e) => {
                  if (!dragFileId) return;
                  e.preventDefault();
                  moveFiles(dragPayload(dragFileId), null);
                  setDropFolderId(undefined);
                }}
              >
                <span className="material-symbols-outlined">snippet_folder</span>
                <span>Unsorted</span>
                <span className="pv-folder-count">{files.filter(f => !f.folderId).length}</span>
              </div>

              {folders.map(folder => (
                <div
                  key={folder.folderId}
                  className={`pv-folder-item ${selectedFolderId === folder.folderId ? 'active' : ''} ${dragFileId && dropFolderId === folder.folderId ? 'drop-target' : ''}`}
                  onClick={() => setSelectedFolderId(folder.folderId)}
                  onDragOver={(e) => { if (dragFileId) { e.preventDefault(); setDropFolderId(folder.folderId); } }}
                  onDragLeave={() => setDropFolderId(prev => prev === folder.folderId ? undefined : prev)}
                  onDrop={(e) => {
                    if (!dragFileId) return;
                    e.preventDefault();
                    moveFiles(dragPayload(dragFileId), folder.folderId);
                    setDropFolderId(undefined);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setFolderContextMenu({ x: e.clientX, y: e.clientY, folder });
                  }}
                >
                  <span className="material-symbols-outlined">folder</span>
                  {renamingFolder === folder.folderId ? (
                    <input
                      className="pv-rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameFolder(folder.folderId);
                        if (e.key === 'Escape') setRenamingFolder(null);
                      }}
                      onBlur={() => setRenamingFolder(null)}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span>{folder.name}</span>
                  )}
                  <span className="pv-folder-count">
                    {files.filter(f => f.folderId === folder.folderId).length}
                  </span>
                </div>
              ))}

              {/* Folder context menu */}
              {folderContextMenu && (
                <div
                  className="pv-context-menu"
                  style={{ position: 'fixed', left: folderContextMenu.x, top: folderContextMenu.y, zIndex: 3000 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button onClick={() => {
                    setRenamingFolder(folderContextMenu.folder.folderId);
                    setRenameValue(folderContextMenu.folder.name);
                    setFolderContextMenu(null);
                  }}>
                    <span className="material-symbols-outlined">edit</span>
                    Rename
                  </button>
                  <button
                    onClick={() => handleDeleteFolder(folderContextMenu.folder.folderId)}
                    style={{ color: '#e11d48' }}
                  >
                    <span className="material-symbols-outlined">delete</span>
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* File Grid */}
            <div
              className={`pv-file-grid ${isDragOver ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="pv-file-grid-header">
                {selectedFiles.size > 0 ? (
                  <>
                    <span className="pv-selection-count">
                      {selectedFiles.size} selected
                    </span>
                    <button className="pv-select-all-btn" onClick={selectAllFiles}>
                      Select All ({filteredFiles.length})
                    </button>
                    <button className="pv-clear-selection-btn" onClick={clearSelection}>
                      <span className="material-symbols-outlined">close</span>
                    </button>
                    <div className="pv-selection-divider" />
                    <button
                      className="pv-mass-move-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setMoveMenu({ fileIds: [...selectedFiles], x: rect.left, y: rect.bottom + 4 });
                      }}
                    >
                      <span className="material-symbols-outlined">drive_file_move</span>
                      Move
                    </button>
                    <button className="pv-mass-download-btn" onClick={handleMassDownload}>
                      <span className="material-symbols-outlined">download</span>
                      Download selected
                    </button>
                    <button
                      className={`pv-download-all-btn ${isDownloadingZip ? 'loading' : ''}`}
                      onClick={handleDownloadAllZip}
                      disabled={files.length === 0 || isDownloadingZip}
                      title="Download every project file as one ZIP"
                    >
                      <span className="material-symbols-outlined">
                        {isDownloadingZip ? 'progress_activity' : 'folder_zip'}
                      </span>
                      {isDownloadingZip ? 'Preparing ZIP...' : 'Download all (.zip)'}
                    </button>
                    <button
                      className="pv-mass-delete-btn"
                      onClick={() => setMassDeleteConfirm(true)}
                    >
                      <span className="material-symbols-outlined">delete</span>
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <span className="pv-file-count">
                      {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''}
                    </span>
                    {zipDownloadError && (
                      <span className="pv-zip-download-error" title={zipDownloadError}>
                        {zipDownloadError}
                      </span>
                    )}
                    <button
                      className={`pv-download-all-btn ${isDownloadingZip ? 'loading' : ''}`}
                      onClick={handleDownloadAllZip}
                      disabled={files.length === 0 || isDownloadingZip}
                      title="Download every project file as one ZIP"
                    >
                      <span className="material-symbols-outlined">
                        {isDownloadingZip ? 'progress_activity' : 'folder_zip'}
                      </span>
                      {isDownloadingZip ? 'Preparing ZIP...' : 'Download all (.zip)'}
                    </button>
                    <button
                      className="pv-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <span className="material-symbols-outlined">upload_file</span>
                      Upload
                    </button>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files?.length) handleFileUpload(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {/* Mass delete confirmation modal */}
              {massDeleteConfirm && (
                <div className="pv-mass-delete-modal" onClick={() => setMassDeleteConfirm(false)}>
                  <div className="pv-mass-delete-dialog" onClick={(e) => e.stopPropagation()}>
                    <span className="material-symbols-outlined pv-delete-icon">delete_forever</span>
                    <h3>Delete {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''}?</h3>
                    <p>This action cannot be undone.</p>
                    <div className="pv-mass-delete-actions">
                      <button className="pv-mass-cancel" onClick={() => setMassDeleteConfirm(false)}>Cancel</button>
                      <button className="pv-mass-confirm-delete" onClick={handleMassDelete}>Delete</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload progress indicators */}
              {Object.entries(uploadProgress).map(([key, { percent, name }]) => (
                <div key={key} className="pv-upload-progress">
                  <span className="material-symbols-outlined">upload</span>
                  <span className="pv-upload-name">{name}</span>
                  <div className="pv-progress-bar">
                    <div className="pv-progress-fill" style={{ width: percent + '%' }} />
                  </div>
                  <span className="pv-progress-pct">{percent}%</span>
                </div>
              ))}

              {filteredFiles.length === 0 && Object.keys(uploadProgress).length === 0 ? (
                <div className="pv-empty-files">
                  <span className="material-symbols-outlined">cloud_upload</span>
                  <p>Drop files here or click Upload</p>
                  <p className="hint">Supports images, videos, 3D models, documents, and more</p>
                </div>
              ) : (
                <FileGrid
                  files={filteredFiles}
                  user={user}
                  api={fileCardApi}
                  dragFileId={dragFileId}
                  dropFolderId={dropFolderId}
                />
              )}

              {/* Move-to-folder popover */}
              {moveMenu && (
                <div
                  className="pv-context-menu"
                  style={{ position: 'fixed', left: moveMenu.x, top: moveMenu.y, zIndex: 3000 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="pv-move-menu-title">
                    Move {moveMenu.fileIds.length > 1 ? `${moveMenu.fileIds.length} files` : 'to'}
                  </div>
                  <button onClick={() => moveFiles(moveMenu.fileIds, null)}>
                    <span className="material-symbols-outlined">snippet_folder</span>
                    Unsorted
                  </button>
                  {folders.map(folder => (
                    <button key={folder.folderId} onClick={() => moveFiles(moveMenu.fileIds, folder.folderId)}>
                      <span className="material-symbols-outlined">folder</span>
                      {folder.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Reaction picker popover */}
              {reactionPicker && (
                <div
                  className="pv-reaction-picker"
                  style={{ position: 'fixed', left: reactionPicker.x, top: reactionPicker.y, zIndex: 3000 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {quickEmojis.map(emoji => (
                    <button
                      key={emoji}
                      className="pv-reaction-option"
                      onClick={() => handleFileReact(reactionPicker.fileId, emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              {isDragOver && (
                <div className="pv-drop-overlay">
                  <span className="material-symbols-outlined">cloud_upload</span>
                  <p>Drop files to upload</p>
                </div>
              )}
            </div>
          </>
        )}

        {currentTab === 'chat' && chatRoom && (
          <div className="pv-chat-tab">
            <ChatWindow
              user={user}
              room={chatRoom}
              messages={messages[chatRoom.roomId] || []}
              isLoadingMessages={!!loadingMessagesByRoom?.[chatRoom.roomId]}
              friends={friends}
              myBubbleColor={myBubbleColor}
              onBack={() => setActiveTab(hasGameDev && !isHiddenTab('overview') ? 'overview' : (tabs[0]?.key || 'files'))}
              onLoadOlderMessages={onLoadOlderMessages}
              loadingOlder={!!paginationByRoom?.[chatRoom.roomId]?.loadingOlder}
              hasMoreOlder={paginationByRoom?.[chatRoom.roomId]?.hasMoreOlder !== false}
            />
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <ProjectFilePreview
          file={previewFile}
          project={project}
          user={user}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* Project Settings Modal */}
      {showSettings && (
        <ProjectSettings
          project={project}
          user={user}
          friends={friends}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

export default ProjectView;
